import React, { useState } from "react";
import { formatMessage } from "model/message";
import CombatController from "controllers/CombatController";
import Button from "./Button";
import {
  Combatants,
  CombatContainer,
  DamageText,
  EnemyHealth,
  PlayerHealth,
} from "./CombatSystem.style";
const debug = require("debug")("component:CombatSystem");

const CombatSystem = (
  { gameState, initialEnemies = [], victory, defeat, onCombatEnd, onDone },
) => {
  const { party } = gameState;
  const { resources } = party;

  /* eslint-disable-next-line no-unused-vars */
  const [enemies, setEnemies] = useState(initialEnemies); // Array of Enemy objects
  const onUpdate = (data) => {
    if (!data) return;
    // Update state based on data received from CombatController
    const { enemyIndex, damage, victory } = data;
    if (victory !== undefined) {
      return handleCombatEnd(victory);
    }

    // Consume ammo if any is available
    if (ammo >= 1) {
      party.resources.delta({ ammo: -1 }, gameState); // Consume ammo
      setAmmo(ammo - 1);
    }

    setPlayerAnimation("gunshot");
    setTimeout(() => setPlayerAnimation("playerIdle"), 200);
    enemyDamageText[enemyIndex] = `-${damage}`;
    setTimeout(() => (enemyDamageText[enemyIndex] = ""), 800);

    // Trigger enemy receive damage animation
    const newEnemyAnimations = [...enemyAnimations];
    newEnemyAnimations[enemyIndex] = "enemyDamage";
    setEnemyAnimations(newEnemyAnimations);
    setTimeout(() => {
      newEnemyAnimations[enemyIndex] = "enemyIdle";
      setEnemyAnimations(newEnemyAnimations);
    }, 200);

    setTimeout(handleEnemyAttack, 1000); // Simulate enemy attack delay
  };

  /* eslint-disable-next-line no-unused-vars */
  const [controller, setController] = useState(
    new CombatController(enemies, party, onUpdate),
  );
  const [playerHealth, setPlayerHealth] = useState(
    resources.attribs.health.value,
  );
  const [ammo, setAmmo] = useState(resources.attribs.ammo.value);
  const [combatResult, setCombatResult] = useState(null); // ['victory', 'defeat']
  const [playerDamageText, setPlayerDamageText] = useState("");
  const [enemyDamageText] = useState(enemies.map(() => "")); // Array to store damage text for each enemy (initialize empty)
  const [playerAnimation, setPlayerAnimation] = useState("playerIdle");
  const [enemyAnimations, setEnemyAnimations] = useState(
    new Array(initialEnemies.length).fill("enemyIdle"),
  );

  const handleEnemyAttack = async () => {
    let totalPlayerDamage = 0;

    for (const [index, enemy] of enemies.entries()) {
      if (!enemy.isDead) {
        totalPlayerDamage += controller.handleEnemyTurn(enemy, gameState);

        // Log the enemy for debugging
        debug(enemy);

        // Enemy attack animation
        const newEnemyAnimations = enemyAnimations.map((
          anim,
          idx,
        ) => (idx === index ? "enemyAttack" : anim));
        setEnemyAnimations(newEnemyAnimations);

        await new Promise((resolve) => setTimeout(resolve, 200)); // Wait for animation

        const resetAnimations = newEnemyAnimations.map((
          anim,
          idx,
        ) => (idx === index ? "enemyIdle" : anim));
        setEnemyAnimations(resetAnimations);

        // Optionally handle enemy special abilities here
      }
    }

    // Apply total damage to player after all enemy attacks
    setPlayerHealth((prevHealth) =>
      Math.max(prevHealth - totalPlayerDamage, 0)
    );
    setPlayerDamageText(`-${totalPlayerDamage}`);

    setTimeout(() => {
      setPlayerDamageText("");
      setPlayerAnimation("playerIdle");
    }, 800);

    // Trigger player receive damage animation
    setPlayerAnimation("playerDamage");
  };

  const handleCombatEnd = (playerWon) => {
    const health = playerHealth;
    const oldHealth = resources.attribs.health.value;
    const healthLost = Math.round(health - oldHealth);
    const description = playerWon
      ? victory || "silence. the weight of the deed heavy in the air."
      : defeat || "the world fades to black.";
    const message =
      formatMessage(description, { cost: { health: healthLost } }).label;

    setCombatResult(victory ? "victory" : "defeat");
    onCombatEnd(description, message);
  };

  const handleContinue = () => {
    onDone(null);
  };

  const handleFlee = () => {
    // TODO: Dynamic Flee cost encapsulated in an Action
    const health = party.resources.attribs.health.value / 2;
    const coins = party.resources.attribs.coins.value / 2;
    const water = party.resources.attribs.water.value / 2;
    const food = party.resources.attribs.food.value / 2;
    party.resources.delta(
      {
        health: -health,
        coins: -coins,
        water: -water,
        food: -food,
      },
      gameState,
    );
    const description = "you flee from the battle, leaving much behind.";
    const message = formatMessage(description, {
      cost: { health, coins, water, food },
    }).label;
    onCombatEnd(description, message);
    onDone(null);
  };

  return (
    <CombatContainer>
      <Combatants>
        <PlayerHealth animate={playerAnimation}>
          Health: {playerHealth}{" "}
          {playerDamageText && <DamageText>{playerDamageText}</DamageText>}
        </PlayerHealth>
        <div>
          {enemies.map((enemy, index) => {
            return (
              <EnemyHealth key={enemy.id} animate={enemyAnimations[index]}>
                <Button
                  text={controller?.isPlayerTurn
                    ? (ammo ? "Shoot" : "Club")
                    : ammo
                    ? "Reloading"
                    : "Bracing"}
                  onClick={() => controller.handlePlayerAttack(index)}
                  forceDisabled={!controller?.isPlayerTurn || enemy.isDead ||
                    combatResult}
                  tooltips={[]}
                  cooldown={1}
                  isAccept={false}
                />
                {enemy.name}:{" "}
                {enemy.health
                  ? `${enemy.health}/${enemy.maxHealth}`
                  : "Defeated"}
                {enemyDamageText[index] && (
                  <DamageText>{enemyDamageText[index]}</DamageText>
                )}
              </EnemyHealth>
            );
          })}
        </div>
      </Combatants>

      {/* Option to Exit or Continue after Combat Ends */}
      {combatResult && (
        <Button
          text={"Continue"}
          onClick={() => handleContinue(combatResult)}
        />
      )}
      {!combatResult && <Button text="Flee" onClick={() => handleFlee()} />}
    </CombatContainer>
  );
};

export default CombatSystem;
